<template>
  <Layout>
    <PageHeader :title="title" />

    <!-- Header Info Card -->
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div
              class="d-flex justify-content-between align-items-start flex-wrap gap-2"
            >
              <div>
                <h5 class="card-title mb-2">
                  {{ selected_id ? "Form Edit Soal" : "Form Tambah Soal" }}
                </h5>
                <p class="mb-1">
                  <span class="text-muted small">Kompetensi</span><br />
                  <strong>{{
                    kompetensi?.kompetensi?.nama_kompetensi || "-"
                  }}</strong>
                </p>
                <p class="mb-0">
                  <span class="text-muted small">Level Proficiency</span><br />
                  <strong>{{
                    kompetensi?.level_kompetensi?.nama_level || "-"
                  }}</strong>
                </p>
              </div>
              <span class="badge bg-primary fs-6">{{ formData.tipe }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Seksi 1: Informasi Soal -->
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <h6 class="card-title border-bottom pb-2 mb-3">
              1. Informasi Soal
            </h6>

            <!-- Tipe Soal -->
            <div class="mb-3">
              <label class="form-label fw-semibold">Tipe Soal</label>
              <div class="d-flex flex-wrap gap-2">
                <div
                  v-for="t in tipeOptions"
                  :key="t"
                  class="border rounded px-3 py-2 small"
                  :class="[
                    formData.tipe === t
                      ? 'bg-primary text-white border-primary'
                      : 'bg-light text-secondary',
                    selected_id ? 'disabled-item' : 'cursor-pointer',
                  ]"
                  @click="!selected_id && setTipe(t)"
                  :disabled="isReadOnly"
                >
                  {{ t }}
                </div>
              </div>
            </div>

            <div class="row">
              <!-- Nomor Urut -->
              <div class="col-md-3 mb-3">
                <label class="form-label fw-semibold">Nomor Urut</label>
                <input
                  type="number"
                  class="form-control"
                  v-model.number="formData.nomor_urut"
                  min="1"
                  readonly
                  disabled
                />
                <small class="text-muted">
                  Nomor urut otomatis mengikuti urutan terakhir.
                </small>
              </div>

              <!-- Referensi Asesmen Objektif -->
              <div class="col-md-9 mb-3">
                <label class="form-label fw-semibold"> Asesmen Objektif </label>
                <v-select
                  v-model="formData.objektif"
                  :options="optionObjektif"
                  label="deskripsi"
                  placeholder="Cari referensi asesmen..."
                  :disabled="isReadOnly"
                >
                  <template v-slot:selected-option="option">
                    {{ option.deskripsi }} - {{ option.scope }}
                  </template>
                  <template v-slot:option="option">
                    {{ option.deskripsi }} - {{ option.scope }}
                  </template>
                </v-select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Seksi 2: Pertanyaan -->
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <h6 class="card-title border-bottom pb-2 mb-3">2. Pertanyaan</h6>

            <!-- Studi Kasus -->
            <div
              class="mb-3"
              v-if="isLevel3 || isLevel4 || formData.tipe === 'Text' || formData.studi_kasus"
            >
              <label class="form-label fw-semibold">
                Studi Kasus
                <span class="text-muted fw-normal">(opsional)</span>
              </label>
              <textarea
                class="form-control"
                rows="4"
                v-model="formData.studi_kasus"
                placeholder="Masukkan teks studi kasus di sini..."
                :disabled="isReadOnly"
              />
            </div>

            <div class="mb-3">
              <label class="form-label fw-semibold">Teks Pertanyaan</label>
              <textarea
                class="form-control"
                rows="4"
                v-model="formData.pertanyaan"
                placeholder="Masukkan teks pertanyaan di sini..."
                :disabled="isReadOnly"
              />
            </div>

            <div class="mb-3">
              <label class="form-label fw-semibold">
                Gambar Pertanyaan
                <span class="text-muted fw-normal">(opsional)</span>
              </label>

              <input
                type="file"
                id="formImage"
                class="form-control"
                accept="image/*"
                @change="inputFilePhoto"
                :disabled="isReadOnly"
                v-if="!isReadOnly"
              />

              <div id="uploadLoading" class="mt-1"></div>

              <div
                v-if="imagePreview"
                class="mt-2 position-relative d-inline-block"
              >
                <img
                  :src="imagePreview"
                  class="img-thumbnail"
                  style="max-height: 180px; object-fit: cover; cursor: pointer"
                  title="Klik untuk preview"
                  data-bs-toggle="modal"
                  data-bs-target="#modalPreviewGambar"
                />
                <button
                  type="button"
                  class="btn btn-danger btn-sm position-absolute top-0 end-0 rounded-circle"
                  style="
                    width: 24px;
                    height: 24px;
                    padding: 0;
                    line-height: 1;
                    transform: translate(50%, -50%);
                  "
                  @click="hapusGambar"
                  title="Hapus gambar"
                  v-if="!isReadOnly"
                >
                  ×
                </button>
              </div>
            </div>

            <div class="mb-0">
              <label class="form-label fw-semibold">Standar Kompetensi</label>
              <input
                type="text"
                class="form-control"
                v-model="formData.standar_kompetensi"
                placeholder="Contoh: SK 3.1.1 – Komunikasi verbal efektif"
                :disabled="isReadOnly"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Seksi 3: Jawaban / Kunci Jawaban -->
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <h6 class="card-title border-bottom pb-2 mb-3">
              {{ formData.tipe === 'Text' ? '3. Kunci Jawaban' : '3. Pilihan Jawaban' }}
            </h6>
            <p class="text-muted small mb-3">
              <span v-if="formData.tipe === 'Text'"
                >Masukkan kata kunci atau poin-poin kunci jawaban soal uraian/teks.</span
              >
              <span v-else-if="formData.tipe === 'Menjodohkan'"
                >Masukkan pasangan pernyataan dan jawaban yang sesuai.</span
              >
              <span v-else
                >Tandai jawaban yang benar dengan tombol
                <strong>Tandai Benar</strong>.</span
              >
            </p>

            <!-- Kunci Jawaban untuk Soal Text / Esai -->
            <div v-if="formData.tipe === 'Text'">
              <div
                v-for="(jaw, index) in formData.jawaban"
                :key="index"
                class="card mb-3 border shadow-none bg-light"
              >
                <div class="card-body p-3">
                  <div class="d-flex align-items-center justify-content-between mb-2">
                    <div class="d-flex align-items-center gap-2">
                      <span
                        class="badge bg-primary rounded-circle d-flex align-items-center justify-content-center fw-bold"
                        style="width: 28px; height: 28px; font-size: 13px"
                      >
                        {{ index + 1 }}
                      </span>
                      <span class="fw-semibold text-dark">
                        Kunci Jawaban {{ index + 1 }}
                      </span>
                    </div>

                    <button
                      v-if="!isReadOnly"
                      type="button"
                      class="btn btn-outline-danger btn-sm"
                      @click="hapusJawaban(index)"
                      :disabled="formData.jawaban?.length <= 1"
                      title="Hapus Kunci Jawaban"
                    >
                      <i class="fa fa-times"></i>
                    </button>
                  </div>

                  <textarea
                    class="form-control bg-white"
                    rows="3"
                    v-model="jaw.text_jawaban"
                    :placeholder="'Masukkan teks kunci jawaban ' + (index + 1) + '...'"
                    :disabled="isReadOnly"
                  />
                </div>
              </div>

              <button
                v-if="!isReadOnly"
                type="button"
                class="btn btn-outline-primary btn-sm mt-2 w-100"
                @click="tambahJawaban"
              >
                <i class="fa fa-plus me-1"></i> Tambah Kunci Jawaban
              </button>
            </div>

            <!-- Pilihan Ganda & Benar Salah -->
            <div v-else-if="formData.tipe !== 'Menjodohkan'">
              <div
                v-for="(jaw, index) in formData.jawaban"
                :key="index"
                class="d-flex align-items-center gap-2 mb-2 p-2 rounded border"
                :class="
                  jaw.skor === 1
                    ? 'border-success bg-success bg-opacity-10'
                    : 'bg-light'
                "
              >
                <!-- Kode Badge -->
                <span
                  class="badge rounded-circle d-flex align-items-center justify-content-center fw-bold"
                  :class="jaw.skor === 1 ? 'bg-success' : 'bg-secondary'"
                  style="
                    width: 30px;
                    height: 30px;
                    flex-shrink: 0;
                    font-size: 13px;
                  "
                >
                  {{ jaw.kode_jawaban }}
                </span>

                <!-- Input Teks -->
                <input
                  type="text"
                  class="form-control form-control-sm"
                  :class="jaw.skor === 1 ? 'border-success' : ''"
                  v-model="jaw.text_jawaban"
                  :placeholder="'Teks jawaban ' + jaw.kode_jawaban"
                  :readonly="formData.tipe === 'Benar Salah'"
                  :disabled="isReadOnly"
                />

                <!-- Toggle Benar -->
                <button
                  type="button"
                  class="btn btn-sm text-nowrap"
                  :class="
                    jaw.skor === 1 ? 'btn-success' : 'btn-outline-secondary'
                  "
                  @click="toggleSkor(index)"
                  style="min-width: 110px"
                  :disabled="isReadOnly"
                >
                  {{ jaw.skor === 1 ? "✓ Benar" : "Tandai Benar" }}
                </button>

                <!-- Hapus -->
                <button
                  v-if="formData.tipe !== 'Benar Salah' && !isReadOnly"
                  type="button"
                  class="btn btn-sm btn-outline-danger"
                  @click="hapusJawaban(index)"
                  :disabled="formData.jawaban?.length <= 2"
                >
                  ×
                </button>
              </div>

              <button
                v-if="formData.tipe !== 'Benar Salah' && !isReadOnly"
                type="button"
                class="btn btn-outline-primary btn-sm mt-2 w-100"
                @click="tambahJawaban"
              >
                + Tambah Pilihan Jawaban
              </button>
            </div>

            <!-- Menjodohkan -->
            <div v-else>
              <div
                v-for="(jaw, index) in formData.jawaban"
                :key="index"
                class="d-flex align-items-center gap-2 mb-2 p-2 rounded border bg-light"
              >
                <input
                  type="text"
                  class="form-control form-control-sm"
                  v-model="jaw.text_jawaban"
                  :placeholder="'Text Jawaban ' + (index + 1)"
                  :disabled="isReadOnly"
                />

                <!-- Toggle Benar -->
                <button
                  type="button"
                  class="btn btn-sm text-nowrap"
                  :class="
                    jaw.skor === 1 ? 'btn-success' : 'btn-outline-secondary'
                  "
                  @click="toggleSkor(index)"
                  style="min-width: 110px"
                  :disabled="isReadOnly"
                >
                  {{ jaw.skor === 1 ? "✓ Benar" : "Tandai Benar" }}
                </button>

                <button
                  v-if="!isReadOnly"
                  type="button"
                  class="btn btn-sm btn-outline-danger"
                  @click="hapusJawaban(index)"
                  :disabled="formData.jawaban?.length <= 2"
                >
                  ×
                </button>
              </div>

              <button
                type="button"
                class="btn btn-outline-primary btn-sm mt-2 w-100"
                @click="tambahJawaban"
                v-if="!isReadOnly"
              >
                + Tambah Pasangan
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Seksi 4: Referensi & Teori -->
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <h6 class="card-title border-bottom pb-2 mb-3">
              4. Referensi &amp; Teori
            </h6>

            <div class="mb-3">
              <label class="form-label fw-semibold">Konsep / Teori</label>
              <textarea
                class="form-control"
                rows="3"
                v-model="formData.konsep_teori"
                placeholder="Tulis konsep atau teori yang mendasari soal ini..."
                :disabled="isReadOnly"
              />
            </div>

            <div class="mb-0">
              <label class="form-label fw-semibold">Referensi</label>
              <input
                type="text"
                class="form-control"
                v-model="formData.referensi"
                placeholder="Contoh: Modul Komunikasi Bab 3, hal. 45"
                :disabled="isReadOnly"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Seksi 5: Catatan -->
    <div class="row" v-if="isValidator || catatanVerifikasiList.length">
      <div class="col-12">
        <div class="card border-warning">
          <div class="card-body bg-light">
            <h6 class="card-title border-bottom pb-2 mb-3 text-warning">
              <i class="mdi mdi-shield-check-outline me-1"></i>
              Verifikasi Soal
            </h6>

            <!-- APPROVED -->
            <div
              class="d-flex align-items-center gap-2 mb-3"
              v-if="isValidator"
            >
              <label class="form-label fw-semibold mb-0">Approved</label>
              <div class="form-check form-switch mb-0">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="approved"
                  :checked="formData.approved === true"
                  @change="formData.approved = $event.target.checked"
                />
              </div>
            </div>

            <div class="alert alert-info py-2 small" v-if="isValidator">
              <i class="fa fa-exclamation-triangle me-1"></i>
              Tekan <strong>Approved</strong> jika setuju. Jika ada perbaikan, tidak perlu menekan tombol tersebut.
            </div>

            <!-- INFO VALIDATOR -->
            <div class="alert alert-warning py-2 small" v-if="isValidator">
              Anda sedang dalam mode <strong>Validator</strong>. Semua data soal
              bersifat <strong>read-only</strong>. Silakan isi catatan jika
              diperlukan.
            </div>

            <!-- CATATAN -->
            <div class="mb-0">
              <div class="d-flex justify-content-between align-items-center">
                <label class="form-label fw-semibold mb-1">
                  Catatan Verifikasi
                </label>

                <!-- BUTTON CLEAR -->
                <button
                  v-if="isValidator"
                  class="btn btn-sm btn-outline-danger"
                  :disabled="!formData.catatan"
                  @click="clearCatatan"
                  type="button"
                >
                  <i class="fa fa-times me-1"></i> Hapus
                </button>
              </div>

              <textarea
                v-if="isValidator"
                class="form-control border-warning"
                rows="3"
                v-model="formData.catatan"
                placeholder="Tulis catatan atau feedback untuk soal ini..."
              />

              <ul v-else class="list-group catatan-verifikasi-list">
                <li
                  v-for="(catatan, index) in catatanVerifikasiList"
                  :key="catatan.id || index"
                  class="list-group-item"
                >
                  <div
                    class="d-flex justify-content-between align-items-start gap-2 mb-1"
                  >
                    <strong class="small">
                      {{ catatan.validator || "Validator " + (index + 1) }}
                    </strong>
                    <span class="badge bg-warning text-dark">Catatan</span>
                  </div>
                  <div class="small text-body">
                    {{ catatan.text }}
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer Actions -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="d-flex justify-content-end gap-2">
          <button
            type="button"
            class="btn btn-secondary"
            @click="$router.back()"
          >
            Batal
          </button>
          <button
            type="button"
            class="btn btn-primary btn-save"
            @click="simpanSoal"
          >
            <span v-if="!isValidator">Simpan Soal</span>
            <span v-else>Verifikasi</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Preview Gambar -->
    <div
      class="modal fade"
      id="modalPreviewGambar"
      tabindex="-1"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h6 class="modal-title">Preview Gambar</h6>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>
          <div class="modal-body text-center p-2">
            <img
              :src="imagePreview"
              class="img-fluid rounded"
              style="max-height: 70vh; object-fit: contain"
            />
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.card.border-warning {
  border-width: 1.5px;
}

textarea:disabled {
  background-color: #f8f9fa;
  cursor: not-allowed;
}

.catatan-verifikasi-list .list-group-item {
  border-color: #f1d88a;
}
</style>

<script>
import Layout from "@/router/layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import axios from "axios";
import $ from "jquery";
import Swal from "sweetalert2";

export default {
  page: {
    title: "Tambah Soal",
    meta: [{ name: "description", content: appConfig.description }],
  },
  components: { Layout, PageHeader },
  computed: {
    isReadOnly() {
      return this.isValidator === true || this.isValidator === "true";
    },
    catatanVerifikasiList() {
      return this.normalizeCatatanList(this.formData.catatan);
    },
    detectedLevel() {
      if (this.$route.query.level) return this.$route.query.level;
      if (this.kompetensi?.idlevel_kompetensi) return this.kompetensi.idlevel_kompetensi;
      if (this.kompetensi?.level_kompetensi?.idlevel_kompetensi) return this.kompetensi.level_kompetensi.idlevel_kompetensi;
      const nama = this.kompetensi?.level_kompetensi?.nama_level || "";
      const match = nama.match(/\d+/);
      return match ? match[0] : null;
    },
    isLevel3() {
      return (
        this.$route.query.level == 3 ||
        this.detectedLevel == 3 ||
        this.kompetensi?.idlevel_kompetensi == 3 ||
        this.kompetensi?.level_kompetensi?.idlevel_kompetensi == 3 ||
        (this.kompetensi?.level_kompetensi?.nama_level &&
          this.kompetensi.level_kompetensi.nama_level.includes("3"))
      );
    },
    isLevel4() {
      return (
        this.$route.query.level == 4 ||
        this.detectedLevel == 4 ||
        this.kompetensi?.idlevel_kompetensi == 4 ||
        this.kompetensi?.level_kompetensi?.idlevel_kompetensi == 4 ||
        (this.kompetensi?.level_kompetensi?.nama_level &&
          this.kompetensi.level_kompetensi.nama_level.includes("4"))
      );
    },
  },
  data() {
    return {
      title: this.$route.params.idSoal ? "Edit Soal" : "Tambah Soal",
      idKompetensi: this.$route.params.id,
      selected_id: this.$route.params.idSoal,
      kompetensi: null,
      imagePreview: null,
      tipeOptions: ["Pilihan Ganda", "Benar Salah", "Menjodohkan", "Text"],
      formData: {
        idverif_asesmen_objektif: null,
        studi_kasus: "",
        pertanyaan: "",
        pertanyaan_gambar: "",
        tipe: "Pilihan Ganda",
        nomor_urut: 1,
        konsep_teori: "",
        referensi: "",
        standar_kompetensi: "",
        objektif: null,
        jawaban: [
          { kode_jawaban: "A", text_jawaban: "", skor: 0 },
          { kode_jawaban: "B", text_jawaban: "", skor: 0 },
          { kode_jawaban: "C", text_jawaban: "", skor: 0 },
          { kode_jawaban: "D", text_jawaban: "", skor: 0 },
        ],
        catatan: null,
        approved: false,
        is_validator:
          localStorage.is_validator === "true" ||
          localStorage.is_validator === true,
      },
      isValidator: localStorage.is_validator == "true",
      optionObjektif: [],
    };
  },
  async mounted() {
    await this.getKompetensi();
    if (this.selected_id) {
      this.fetchData();
    } else {
      this.getNextNomorUrut();
      if (this.isLevel4) {
        this.setTipe("Text");
      }
    }
  },
  methods: {
    async getNextNomorUrut() {
      try {
        const res = await axios({
          method: "get",
          url:
            process.env.VUE_APP_BACKEND_URL_VERSION +
            "verifikasi/kompetensi-level/" +
            this.idKompetensi +
            "/soal",
          params: {
            per_page: 1,
            page: 1,
            sort_by: "nomor_urut",
            sort_direction: "desc",
          },
          headers: {
            Accept: "application/json",
            Authorization: "Bearer " + localStorage.access_token,
          },
        });

        const lastSoal = res.data?.data?.data?.[0];
        const lastNomorUrut = Number(lastSoal?.nomor_urut) || 0;
        this.formData.nomor_urut = lastNomorUrut + 1;
      } catch (err) {
        console.error("Error fetching nomor urut:", err);
      }
    },

    async fetchData() {
      try {
        const res = await axios({
          method: "get",
          url:
            process.env.VUE_APP_BACKEND_URL_VERSION +
            "verifikasi/kompetensi-level/" +
            this.idKompetensi +
            "/soal/" +
            this.selected_id,
          headers: {
            Accept: "application/json",
            Authorization: "Bearer " + localStorage.access_token,
          },
        });
        const data = res.data.data;
        if (data) {
          this.formData.idverif_asesmen_objektif =
            data.idverif_asesmen_objektif;
          this.formData.objektif = data.objektif;
          this.formData.studi_kasus = data.studi_kasus || "";
          this.formData.pertanyaan = data.pertanyaan;
          this.formData.tipe = data.tipe;
          this.formData.nomor_urut = Number(data.nomor_urut);
          this.formData.konsep_teori = data.konsep_teori;
          this.formData.referensi = data.referensi;
          this.formData.standar_kompetensi = data.standar_kompetensi;
          this.formData.catatan = data.catatan;
          this.formData.approved = data.status_verifikasi == 'APPROVED';

          if (data.pertanyaan_gambar) {
            this.formData.pertanyaan_gambar = data.pertanyaan_gambar;
            this.imagePreview =
              process.env.VUE_APP_BACKEND_URL + data.pertanyaan_gambar;
          }

          if (data.keyword_soal && Array.isArray(data.keyword_soal) && data.keyword_soal.length > 0) {
            this.formData.jawaban = data.keyword_soal.map((k) => ({
              idverif_keyword_soal: k.idverif_keyword_soal,
              text_jawaban: k.keyword || k.text || "",
              skor: 1,
            }));
          } else if (data.jawaban && Array.isArray(data.jawaban) && data.jawaban.length > 0) {
            this.formData.jawaban = data.jawaban.map((j) => ({
              kode_jawaban: j.kode_jawaban,
              text_jawaban: j.text_jawaban,
              skor: Number(j.skor),
            }));
            this.normalizeSingleCorrectAnswer();
          } else if (data.tipe === "Text") {
            this.formData.jawaban = [
              { text_jawaban: "", skor: 1 },
              { text_jawaban: "", skor: 1 },
            ];
          }
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    },

    async getKompetensi() {
      const data = await axios({
        method: "get",
        url:
          process.env.VUE_APP_BACKEND_URL_VERSION +
          "verifikasi/kompetensi-level/" +
          this.idKompetensi,
        headers: {
          Authorization: `Bearer ${localStorage.access_token}`,
          Accept: "Application/json",
        },
      }).then((r) => r.data.data);
      if (data) {
        this.kompetensi = data;
        this.optionObjektif = this.kompetensi.objektif;
      }
    },

    async getAsesmenObjektif(search = "", loading) {
      const data = await axios({
        method: "get",
        url:
          process.env.VUE_APP_BACKEND_URL_VERSION +
          "referensi/asesmen-objektif/all",
        params: { search, limit: 100 },
        headers: {
          Authorization: `Bearer ${localStorage.access_token}`,
          Accept: "Application/json",
        },
      }).then((r) => r.data.data);
      if (data) this.optionObjektif = data;
      if (loading) loading(false);
    },

    setTipe(tipe) {
      this.formData.tipe = tipe;
      if (tipe === "Benar Salah") {
        this.formData.jawaban = [
          { kode_jawaban: "A", text_jawaban: "Benar", skor: 0 },
          { kode_jawaban: "B", text_jawaban: "Salah", skor: 0 },
        ];
      } else if (tipe === "Pilihan Ganda") {
        this.formData.jawaban = [
          { kode_jawaban: "A", text_jawaban: "", skor: 0 },
          { kode_jawaban: "B", text_jawaban: "", skor: 0 },
          { kode_jawaban: "C", text_jawaban: "", skor: 0 },
          { kode_jawaban: "D", text_jawaban: "", skor: 0 },
        ];
      } else if (tipe === "Menjodohkan") {
        this.formData.jawaban = [
          { text_jawaban: "", pasangan: "", skor: 0 },
          { text_jawaban: "", pasangan: "", skor: 0 },
        ];
      } else if (tipe === "Text") {
        this.formData.jawaban = [
          { text_jawaban: "", skor: 1 },
          { text_jawaban: "", skor: 1 },
        ];
      } else {
        this.formData.jawaban = [];
      }
    },

    tambahJawaban() {
      if (this.formData.tipe === "Menjodohkan") {
        this.formData.jawaban.push({ text_jawaban: "", pasangan: "", skor: 0 });
      } else if (this.formData.tipe === "Text") {
        this.formData.jawaban.push({
          text_jawaban: "",
          skor: 1,
        });
      } else {
        const KODE_LIST = "ABCDEFGHIJKLMNOP".split("");
        const usedKode = this.formData.jawaban.map((j) => j.kode_jawaban);
        const nextKode =
          KODE_LIST.find((k) => !usedKode.includes(k)) ||
          String(this.formData.jawaban?.length + 1);
        this.formData.jawaban.push({
          kode_jawaban: nextKode,
          text_jawaban: "",
          skor: 0,
        });
      }
    },

    hapusJawaban(index) {
      const minCount = this.formData.tipe === "Text" ? 1 : 2;
      if (this.formData.jawaban?.length <= minCount) return;
      this.formData.jawaban.splice(index, 1);

      if (this.formData.tipe !== "Menjodohkan" && this.formData.tipe !== "Text") {
        const KODE_LIST = "ABCDEFGHIJKLMNOP".split("");
        this.formData.jawaban.forEach((jaw, i) => {
          jaw.kode_jawaban = KODE_LIST[i] || String(i + 1);
        });
      }
    },

    isSingleCorrectAnswerTipe() {
      return ["Pilihan Ganda", "Benar Salah", "Menjodohkan"].includes(
        this.formData.tipe
      );
    },

    normalizeSingleCorrectAnswer() {
      if (!this.isSingleCorrectAnswerTipe()) return;

      let hasCorrectAnswer = false;
      this.formData.jawaban = this.formData.jawaban.map((jaw) => {
        const isCorrect = Number(jaw.skor) === 1;
        const skor = isCorrect && !hasCorrectAnswer ? 1 : 0;
        if (skor === 1) hasCorrectAnswer = true;
        return { ...jaw, skor };
      });
    },

    toggleSkor(index) {
      if (this.isSingleCorrectAnswerTipe()) {
        const isCurrentCorrect = this.formData.jawaban[index].skor === 1;
        this.formData.jawaban.forEach((jaw, jawIndex) => {
          jaw.skor = !isCurrentCorrect && jawIndex === index ? 1 : 0;
        });
        return;
      }

      this.formData.jawaban[index].skor =
        this.formData.jawaban[index].skor === 1 ? 0 : 1;
    },

    handleImageUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.formData.pertanyaan_gambar = file;
      const reader = new FileReader();
      reader.onload = (e) => {
        this.imagePreview = e.target.result;
      };
      reader.readAsDataURL(file);
    },

    inputFilePhoto() {
      const self = this;
      if ($("#formImage")[0].files[0]) {
        if ($("#formImage")[0].files[0].size < 2242880) {
          $("#uploadLoading").html(
            '<span class="badge bg-warning p-1"><i class="fa fa-refresh fa-spin"></i> Loading...</span>',
          );
          var urlres = process.env.VUE_APP_BACKEND_URL;
          var FormData = require("form-data");
          var data = new FormData();
          data.append("file", $("#formImage")[0].files[0]);
          var config = {
            method: "post",
            url: process.env.VUE_APP_BACKEND_URL_VERSION + "referensi/upload",
            headers: {
              Authorization: "Bearer " + localStorage.access_token,
            },
            data: data,
          };
          axios(config)
            .then(function (response) {
              var path_file = response.data.data.path_file;
              urlres += path_file;
              $("#uploadLoading").html("");
              self.formData.pertanyaan_gambar = path_file;
              self.imagePreview = urlres;
            })
            .catch(function (error) {
              $("#uploadLoading").html("");
              $("#formImage").val("");

              const response = error.response?.data;
              const errors = response?.data?.error;

              let pesanError = "Gagal mengupload gambar.";
              if (errors && Array.isArray(errors) && errors?.length > 0) {
                pesanError = errors.join("<br>");
              } else if (response?.meta?.message) {
                pesanError = response.meta.message;
              }

              Swal.fire({
                icon: "error",
                title: "Upload Gagal",
                html: pesanError,
                confirmButtonText: "OK",
              });
            });
        } else {
          $("#formImage").val("");
          Swal.fire({
            icon: "warning",
            title: "File Terlalu Besar",
            text: "Ukuran file maksimal 2 MB.",
            confirmButtonText: "OK",
          });
        }
      }
    },

    hapusGambar() {
      this.imagePreview = null;
      this.formData.pertanyaan_gambar = "";
      $("#formImage").val("");
    },

    async clearCatatan() {
      if (!this.formData.catatan) return;

      const result = await Swal.fire({
        icon: "warning",
        title: "Hapus catatan?",
        text: "Catatan yang sudah ditulis akan dihapus.",
        showCancelButton: true,
        confirmButtonText: "Ya, hapus",
        cancelButtonText: "Batal",
      });

      if (result.isConfirmed) {
        this.formData.catatan = "";
      }
    },

    normalizeCatatanList(catatan) {
      if (!catatan) return [];

      if (typeof catatan === "string") {
        const trimmedCatatan = catatan.trim();
        if (!trimmedCatatan) return [];

        try {
          const parsedCatatan = JSON.parse(trimmedCatatan);
          return this.normalizeCatatanList(parsedCatatan);
        } catch {
          return [{ id: 0, validator: null, text: trimmedCatatan }];
        }
      }

      if (Array.isArray(catatan)) {
        return catatan
          .map((item, index) => this.normalizeCatatanItem(item, index))
          .filter((item) => item.text);
      }

      if (typeof catatan === "object") {
        const normalizedItem = this.normalizeCatatanItem(catatan, 0);
        return normalizedItem.text ? [normalizedItem] : [];
      }

      return [];
    },

    normalizeCatatanItem(item, index) {
      if (typeof item === "string") {
        return {
          id: index,
          validator: null,
          text: item.trim(),
        };
      }

      if (!item || typeof item !== "object") {
        return { id: index, validator: null, text: "" };
      }

      return {
        id: item.id || item.idverif_soal_validator || index,
        validator:
          item.nama_validator ||
          item.validator_name ||
          item.validator?.nama ||
          item.validator?.name ||
          item.user?.nama ||
          item.user?.name ||
          null,
        text: String(
          item.catatan ||
          item.catatan_validator ||
          item.note ||
          item.feedback ||
          item.keterangan ||
            "",
        ).trim(),
      };
    },

    validateFormSoal() {
      const errors = [];

      if (!this.formData.pertanyaan?.trim()) {
        errors.push("Teks Pertanyaan wajib diisi.");
      }

      if (this.formData.tipe === "Text") {
        if (!this.formData.jawaban || this.formData.jawaban.length === 0) {
          errors.push("Minimal 1 Kunci Jawaban harus diisi.");
        } else {
          const emptyKeywords = this.formData.jawaban.some(
            (jaw) => !jaw.text_jawaban?.trim()
          );
          if (emptyKeywords) {
            errors.push("Kunci Jawaban tidak boleh ada yang kosong.");
          }
        }
      } else {
        const jawabanKosong = (this.formData.jawaban || []).some(
          (jaw) => !jaw.text_jawaban?.trim(),
        );
        if (jawabanKosong) {
          errors.push("Teks Jawaban tidak boleh ada yang kosong.");
        }
      }

      return errors;
    },

    async simpanSoal() {
      const errors = this.validateFormSoal();
      if (errors.length) {
        Swal.fire({
          icon: "warning",
          title: "Validasi Gagal",
          html: `<ul style="text-align:left;">${errors
            .map((e) => `<li>${e}</li>`)
            .join("")}</ul>`,
        });
        return;
      }

      try {
        Swal.fire({
          title: "Memproses...",
          text: "Sedang menyimpan soal",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });
        const method = this.selected_id ? "put" : "post";
        const url =
          process.env.VUE_APP_BACKEND_URL_VERSION +
          `verifikasi/kompetensi-level/${this.idKompetensi}/soal` +
          (this.selected_id ? `/${this.selected_id}` : "");

        this.formData.idverif_asesmen_objektif =
          this.formData.objektif?.idverif_asesmen_objektif;
        this.normalizeSingleCorrectAnswer();

        const payload = {
          ...this.formData,
        };

        if (this.formData.tipe === "Text") {
          payload.keywords = (this.formData.jawaban || [])
            .map((j) => j.text_jawaban?.trim())
            .filter(Boolean);
        }

        await axios({
          method: method,
          url: url,
          data: payload,
          headers: {
            Authorization: `Bearer ${localStorage.access_token}`,
            Accept: "Application/json",
          },
        });

        Swal.fire({
          icon: "success",
          title: "Berhasil",
          text: "Data soal berhasil disimpan.",
          timer: 1500,
          showConfirmButton: false,
        });

        this.$router.back();
      } catch (err) {
        console.error("Error saving data:", err);

        const resData = err.response?.data;
        let message = "Terjadi kesalahan saat menyimpan data.";

        if (Array.isArray(resData?.data)) {
          message = resData.data.join("<br>");
        } else if (typeof resData?.data === "string") {
          message = resData.data;
        } else if (resData?.meta?.message) {
          message = resData.meta.message;
        }

        Swal.fire({
          icon: "error",
          title: "Gagal",
          html: message,
        });
      }
    },
  },
};
</script>
